/**
 * The raw-upload surface's multipart routes, as the multipart engine's
 * adapter (GDN-4132). The engine owns ordering, retries and abort; this only
 * maps its calls onto the generated mutations.
 */
import { useMemo } from 'react';

import {
  useAbortLargeFileUploadMutation,
  useCompleteLargeFileUploadMutation,
  usePresignLargeFilePartsMutation,
  useStartLargeFileUploadMutation,
} from '../store/hooks';
import type { MultipartAdapter } from '../utils/multipart-upload';

export function useRawMultipartAdapter(organizationId: string): MultipartAdapter {
  const [start] = useStartLargeFileUploadMutation();
  const [presignParts] = usePresignLargeFilePartsMutation();
  const [complete] = useCompleteLargeFileUploadMutation();
  const [abort] = useAbortLargeFileUploadMutation();

  return useMemo<MultipartAdapter>(
    () => ({
      start: async ({ filename }, batch) => {
        const started = await start({
          startRawMultipartUploadRequest: {
            organization_id: organizationId,
            filename,
            ...(batch.uploadId ? { upload_id: batch.uploadId } : {}),
            ...(batch.continuation ? { continuation: true } : {}),
          },
        }).unwrap();
        return {
          batchId: started.upload_id,
          key: started.key,
          multipartUploadId: started.multipart_upload_id,
          maxPartNumber: started.max_part_number,
        };
      },
      presignParts: async (session, partNumbers) => {
        const presigned = await presignParts({
          presignRawMultipartPartsRequest: {
            organization_id: organizationId,
            upload_id: session.batchId,
            key: session.key,
            multipart_upload_id: session.multipartUploadId,
            part_numbers: partNumbers,
          },
        }).unwrap();
        // A missing list is no links: the engine names the part it could not get.
        return (presigned.parts ?? []).map(part => ({
          partNumber: part.part_number,
          url: part.url,
        }));
      },
      complete: async (session, parts) => {
        await complete({
          completeRawMultipartUploadRequest: {
            organization_id: organizationId,
            upload_id: session.batchId,
            key: session.key,
            multipart_upload_id: session.multipartUploadId,
            // The ETag exactly as S3 returned it, quotes included.
            parts: parts.map(part => ({ part_number: part.partNumber, etag: part.etag })),
          },
        }).unwrap();
      },
      abort: async session => {
        await abort({
          abortRawMultipartUploadRequest: {
            organization_id: organizationId,
            upload_id: session.batchId,
            key: session.key,
            multipart_upload_id: session.multipartUploadId,
          },
        }).unwrap();
      },
    }),
    [organizationId, start, presignParts, complete, abort],
  );
}
